import { DrawApi, FileApi } from '@apply/api';
import { Dialog, DialogActions, DialogContent } from '@material-ui/core';
import DataGrid, { Column } from 'devextreme-react/data-grid';
import React, { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { IDrawReceipt } from '@apply/api/src/api/DrawApi';
import ReceiptsModal from './ReceiptsModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/free-regular-svg-icons';
import { faDownload, faFileInvoiceDollar, faTrash } from '@fortawesome/free-solid-svg-icons';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { DrawBudgetCategory } from '../functions';
import CustomStore from 'devextreme/data/custom_store';

interface IReceiptModalProps {
    categories: DrawBudgetCategory[];
    applicationInsights: ApplicationInsights;
    isOpen: boolean;
    close: () => void;
}

const ViewReceiptsModal: React.FC<IReceiptModalProps> = ({ categories, applicationInsights, isOpen, close }) => {
    const drawApi = useMemo(() => new DrawApi.DrawApi(applicationInsights), [applicationInsights]);
    const fileApi = useMemo(() => new FileApi.FileApi(applicationInsights), [applicationInsights]);
    const dataGridRef = useMemo(() => React.createRef<DataGrid>(), []);

    const location = useLocation();
    const drawId = location.pathname.split('/')[2];

    const [openAddEditReceiptsModal, set_openAddEditReceiptsModal] = useState<boolean>(false);
    const [activeReceipt, set_activeReceipt] = useState<IDrawReceipt | undefined>(undefined);

    const customStore = useMemo(
        () =>
            new CustomStore({
                key: 'drawReceiptId',
                load: async () => {
                    const response = await drawApi.getDrawReceipts(drawId);
                    if (response.statusCode !== 200) throw Error('Could not load table data');

                    return response.result;
                },
                insert: (receipt: IDrawReceipt) => {
                    return drawApi.addOrUpdateDrawReceipt(receipt);
                },
                onInserted: () => {
                    dataGridRef?.current?.instance.refresh();
                },
                remove: (drawReceiptId: number) => {
                    return drawApi.deleteReceipt(drawReceiptId);
                },
                onRemoved: () => {
                    dataGridRef?.current?.instance.refresh();
                },
            }),
        [drawId]
    );

    const onSave = (data: IDrawReceipt) => {
        customStore.insert(data);
    };

    const editReceiptClick = (data: IDrawReceipt) => {
        set_activeReceipt(data);
        set_openAddEditReceiptsModal(true);
    };

    const downloadReceiptClick = async (data: IDrawReceipt) => {
        const response = await fileApi.downloadUrl({
            token: data.downloadToken,
        });

        if (response.statusCode !== 200) return;
        window.location.href = response.result.downloadUrl;
    };

    const deleteReceiptClick = (data: IDrawReceipt) => {
        if (data?.drawReceiptId) customStore.remove(data.drawReceiptId);
    };

    return (
        <>
            {drawId && isOpen && (
                <>
                    <div>
                        <Dialog
                            open={isOpen}
                            aria-labelledby='alert-dialog-title'
                            aria-describedby='alert-dialog-description'
                        >
                            <div
                                className='page-header'
                                role='banner'
                            >
                                <h1 style={{ flex: '0 0 100%', maxWidth: '90%' }}>Manage Receipts</h1>
                            </div>
                            <DialogContent>
                                <button
                                    className='button primary admin-slim-button'
                                    onClick={() => set_openAddEditReceiptsModal(true)}
                                    style={{ marginTop: 8 }}
                                >
                                    <FontAwesomeIcon
                                        icon={faFileInvoiceDollar}
                                        size={'1x'}
                                        className='color-white'
                                        style={{ marginLeft: 3, marginRight: 8 }}
                                    />
                                    Add Receipt
                                </button>
                                <DataGrid
                                    ref={dataGridRef}
                                    allowColumnResizing={true}
                                    allowColumnReordering={true}
                                    dataSource={customStore}
                                    className='viewReceiptsModal'
                                    showBorders={true}
                                    showRowLines={true}
                                    remoteOperations={false}
                                >
                                    <Column
                                        dataField='description'
                                        caption='Category'
                                    />
                                    <Column
                                        alignment='right'
                                        dataField='amount'
                                        format='currency'
                                    />
                                    <Column dataField='note' />
                                    <Column
                                        cellRender={(params) => {
                                            return (
                                                <>
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            justifyContent: 'space-around',
                                                        }}
                                                    >
                                                        <button onClick={() => editReceiptClick(params.data)}>
                                                            <FontAwesomeIcon
                                                                icon={faEdit}
                                                                className='admin-icon-button'
                                                            />
                                                        </button>
                                                        <button onClick={() => downloadReceiptClick(params.data)}>
                                                            <FontAwesomeIcon
                                                                icon={faDownload}
                                                                className='admin-icon-button'
                                                            />
                                                        </button>
                                                        <button onClick={() => deleteReceiptClick(params.data)}>
                                                            <FontAwesomeIcon
                                                                icon={faTrash}
                                                                className='admin-icon-button'
                                                            />
                                                        </button>
                                                    </div>
                                                </>
                                            );
                                        }}
                                    />
                                </DataGrid>
                            </DialogContent>
                            <DialogActions>
                                <button
                                    className='button admin-slim-button'
                                    onClick={close}
                                    style={{ marginRight: 16 }}
                                >
                                    Close
                                </button>
                            </DialogActions>
                        </Dialog>
                    </div>
                    <ReceiptsModal
                        isOpen={openAddEditReceiptsModal}
                        categories={categories}
                        onSave={onSave}
                        onClose={() => {
                            set_activeReceipt(undefined);
                            set_openAddEditReceiptsModal(false);
                        }}
                        receipt={activeReceipt}
                    />
                </>
            )}
        </>
    );
};

export default ViewReceiptsModal;
